import React from 'react';
import {useTranslation} from 'react-i18next';
import {Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip} from '@mui/material';
import {IVendorDbaEntity} from 'pwm-shared/src/types/Vendor';
import ConfirmationDialog from 'src/PWM/components/ConfirmationDialog';
import {tokens} from 'src/PWM/locales/tokens';
import {PWMHelpIcon} from 'src/PWM/components/PWMIcons';

const EditVendorDbaModal: React.FC<IEditVendorDbaModalProps> = (props) => {
  const [saveReady, setSaveReady] = React.useState<boolean>(false);
  const [dbaNames, setDbaNames] = React.useState<IVendorDbaEntity[]>([]);

  const {isSaving, onSave, onClose, isOpen} = props;
  
  const {t} = useTranslation();
  
  React.useEffect(() => {
    if (props.dbaNames?.length) setDbaNames(props.dbaNames.map(o => ({...o})));
  }, [props.dbaNames]);

  const doSave = () => {
    if (saveReady) {
      onSave(dbaNames);
    }
  }

  const doCloseCancel = () => {
    if (props.dbaNames?.length) setDbaNames(props.dbaNames.map(o => ({...o})));
    setSaveReady(false);
    props.onClose();
  }

  const checkPreferred = (dbaNameIndex: number) => () => {
    dbaNames[dbaNameIndex].isPreferredName = !dbaNames[dbaNameIndex].isPreferredName;
    setSaveReady(true);
    setDbaNames([...dbaNames]);
  }

  const checkIgnore = (dbaNameIndex: number) => () => {
    dbaNames[dbaNameIndex].isActive = !dbaNames[dbaNameIndex].isActive;
    setSaveReady(true);
    setDbaNames([...dbaNames]);
  }
  
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth={true}
      title={`${t(tokens.interface.edit)} ${t(tokens.interface.dbanames)}`}
      onOk={doSave}
      okButtonText={`${t(tokens.interface.save)}`}
      buttonsDisabled={isSaving || !saveReady}
      closeButtonText={`${saveReady ? t(tokens.interface.cancel) : t(tokens.interface.close)}`}
      message=""
    >
      <TableContainer>
        <Table size="small" stickyHeader={true}>
          <TableHead>
            <TableRow>
              <TableCell>{t(tokens.interface.name)}</TableCell>
              <TableCell align="center">
                <span>{t(tokens.interface.preferred)}</span>
                <Tooltip title={`${t(tokens.interface.dbanamepreferredmessage)}`}>
                  <PWMHelpIcon fontSize="small" />
                </Tooltip>
              </TableCell>
              <TableCell>
                {t(tokens.interface.ignore)}
                <Tooltip title={`${t(tokens.interface.ignoremessage)}`}>
                  <PWMHelpIcon fontSize="small" />
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dbaNames.map(({name, isPreferredName, isActive, id}, index) => (
              <TableRow key={`${name}-${id}`}>
                <TableCell>{name}</TableCell>
                <TableCell align="center">
                  <Checkbox
                    onClick={checkPreferred(index)}
                    size="small"
                    defaultChecked={isPreferredName}
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    onClick={checkIgnore(index)}
                    size="small"
                    defaultChecked={!isActive}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ConfirmationDialog>
  );
};

export default EditVendorDbaModal;

interface IEditVendorDbaModalProps {
  children?: React.ReactNode;
  isOpen: boolean;
  isSaving: boolean;
  dbaNames?: IVendorDbaEntity[];
  onClose(): any;
  onSave(dbaNames: IVendorDbaEntity[]): any;
}